<div class="container">
  <umss-card-header iconName="phosphorCalendarDots" [title]="title()" />

  <div class="disclaimer">
    <umss-icon name="phosphorInfo" size="16px" />
    Click on a date to view details
    @if (!readonly()) {
      and manage availability exceptions
    }
  </div>

  <umss-calendar
    [currentDate]="currentDate"
    [markedDates]="bookedDates"
    [disabledDates]="unavailableDates"
    [clickableDisabledDates]="clickableUnavailableDates"
    [allowDisabledDateClick]="allowSelectAnyDate()"
    [size]="UmssCalendarSize.Regular"
    [fullWidth]="true"
    [markerType]="UmssCalendarMarkerType.Dot"
    [loading]="loading"
    [loadingScope]="'dates'"
    [hideOutsideDays]="true"
    [disabledDateTooltip]="disabledTooltipMessage() || ''"
    markerColor="var(--color-status-error)"
    (monthChange)="onMonthChange($event)"
    (dateClick)="onDateClick($event)"
  />

  <div class="calendar-legend">
    <div class="legend-item">
      <span class="legend-indicator legend-indicator--booked">
        <span class="legend-day-sample">D</span>
        <span class="legend-dot"></span>
      </span>
      <span class="legend-label">Booked</span>
    </div>

    <span class="legend-divider"></span>

    <div class="legend-item">
      <span class="legend-indicator legend-indicator--available">
        <span class="legend-day-sample">D</span>
      </span>
      <span class="legend-label">Available</span>
    </div>

    <span class="legend-divider"></span>

    <div class="legend-item">
      <span class="legend-indicator legend-indicator--unavailable">
        <span class="legend-day-sample">D</span>
      </span>
      <span class="legend-label">Unavailable</span>
    </div>
  </div>

  @if (selectedDateInfo(); as info) {
    <div
      class="date-details-container"
      [@horizontalSlide]="selectedDateAnimIndex()"
    >
      @for (entry of selectedDateInfoEntries(); track entry.date.getTime()) {
        <section class="slide-block">
          <div class="date-details">
            <div class="date-details__header">
              <umss-icon name="phosphorCalendarBlank" size="16px" />
              <span class="date-details__date">
                {{ entry.date | date: 'MMMM d, y' }}
              </span>
            </div>

            <div class="date-details__section">
              <span class="date-details__section-title">Availability</span>

              @if (entry.isUnavailableException) {
                <div class="date-details__exception-row">
                  <div class="date-details__row date-details__row--unavailable">
                    <umss-icon name="phosphorXCircle" size="14px" />
                    <span>
                      Unavailable (exception{{
                        entry.exceptionReason
                          ? ': ' + entry.exceptionReason
                          : ''
                      }})
                    </span>
                    <umss-badge
                      label="Exception"
                      variant="warning"
                      size="small"
                    />
                  </div>
                  @if (!readonly()) {
                    <button
                      type="button"
                      class="date-details__delete-btn"
                      (click)="deleteException(entry.exceptionId!)"
                    >
                      <umss-icon name="phosphorTrash" size="14px" />
                    </button>
                  }
                </div>
              } @else if (entry.availabilitySlots.length > 0) {
                @for (slot of entry.availabilitySlots; track slot.startTime) {
                  <div class="date-details__exception-row">
                    <div class="date-details__row">
                      <umss-icon name="phosphorClock" size="14px" />

                      @if (isAllDay(slot)) {
                        <span>All day</span>
                      } @else {
                        <span>
                          {{ formatTime(slot.startTime) }} –
                          {{ formatTime(slot.endTime) }}
                        </span>
                      }

                      @if (entry.isAvailableException) {
                        <umss-badge
                          label="Exception"
                          variant="warning"
                          size="small"
                        />
                      }
                    </div>

                    @if (entry.isAvailableException && !readonly()) {
                      <button
                        type="button"
                        class="date-details__delete-btn"
                        (click)="deleteException(entry.exceptionId!)"
                      >
                        <umss-icon name="phosphorTrash" size="14px" />
                      </button>
                    }
                  </div>
                }
              } @else {
                <div class="date-details__row date-details__row--unavailable">
                  <umss-icon name="phosphorXCircle" size="14px" />
                  <span>No availability set</span>
                </div>
              }
            </div>

            @if (entry.shifts.length > 0) {
              <div class="date-details__section">
                <span class="date-details__section-title">Shifts</span>

                @for (shift of entry.shifts; track shift.id) {
                  <div class="date-details__shift">
                    <div class="date-details__shift-time">
                      <umss-icon name="phosphorBriefcase" size="14px" />
                      <span>
                        {{ formatTime(shift.startTime) }} –
                        {{ formatTime(shift.endTime) }}
                      </span>

                      @if (!facilityId()) {
                        <button
                          type="button"
                          class="date-details__shift-view-btn"
                          (click)="onShiftClick(shift)"
                        >
                          <umss-icon
                            name="phosphorArrowSquareOut"
                            size="14px"
                          />
                        </button>
                      }
                    </div>
                    <div class="date-details__shift-facility">
                      <span class="date-details__facility-name">
                        {{ shift.facilityName }}
                      </span>

                      @if (facilityId(); as fId) {
                        @if (shift.facilityId === fId) {
                          <umss-badge
                            label="Your facility"
                            variant="success"
                            size="small"
                          />
                        } @else {
                          <umss-badge
                            label="Other facility"
                            variant="neutral"
                            size="small"
                          />
                        }
                      }
                    </div>
                  </div>
                }
              </div>
            }

            @if (!readonly() && !isSelectedDateInPast()) {
              <div class="date-details__actions">
                <span
                  [umssTooltip]="
                    entry.exceptionId !== null
                      ? 'This date already has an exception'
                      : ''
                  "
                  tooltipPosition="top"
                  tooltipSize="small"
                >
                  <umss-button
                    label="Add Exception"
                    variant="Outline"
                    size="Small"
                    leftIconName="phosphorPlusCircle"
                    [disabled]="entry.exceptionId !== null"
                    (onClick)="openAddExceptionDialog()"
                  />
                </span>
              </div>
            }
          </div>
        </section>
      }
    </div>
  }
</div>

<umss-add-availability-exception-dialog
  [open]="showExceptionDialog()"
  [exceptionDate]="selectedDate()"
  [defaultIsUnavailable]="
    (selectedDateInfo()?.availabilitySlots?.length ?? 0) === 0
  "
  (closeDialog)="onExceptionDialogClose($event)"
/>
